/* eslint-disable jsx-a11y/anchor-is-valid */
import { CheckboxCustomEvent, IonButton, IonItem, IonToast, useIonModal, useIonToast } from '@ionic/react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { EnemiesThresholdOptions, FeedPreferences, User, VolumeOptions } from 'model';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { RootState } from '../App';
import { useGetUserBlocksQuery } from '../api/BlockApi';
import {
	useAddProfileImageMutation,
	useDeleteAccountMutation,
	useGetOwnProfileQuery,
	useUpdateEngagementPreferencesMutation,
	useUpdateFeedPreferencesMutation,
	useUpdateNotificationPreferencesMutation,
	useUpdateOwnUserDescriptionMutation
} from '../api/UserApi';
import { Select } from '../components/Forms/Select';

import classNames from 'classnames';
import { Checkbox } from '../components/Forms/Checkbox';
import { FixedField } from '../components/Forms/FixedField';
import { Input } from '../components/Forms/Input.js';
import { TextArea } from '../components/Forms/TextArea';
import { Header } from '../components/Header';
import { HelpPopup } from '../components/HelpPopup';
import { IconButton } from '../components/IconButton';
import { ProfilePic } from '../components/ProfilePic';
import { IconArrowBack, IconHelp, IconLogout } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { TitleColumn } from '../components/TitleColumn';
import { useDebounce } from '../hooks/useDebounce';
import { useImageUpload } from '../hooks/useImageUpload';
import { useLoading } from '../hooks/useQuirkLoading';
import { feedPreferencesHelpText } from '../markdownPages/FeedPreferencesHelpText';
import { LoginState } from '../state/loginSlice';
import { getEngagementHelpMarkdownText, getEngagementLevelsOptions } from '../util/engagement.js';
import { BlockedPage } from './BlockedPage';
import styles from './EditOwnProfile.module.css';
import { TokenPurchase } from '../components/TokenPurchase.js';
import { useGetPublicInformationQuery } from '../api/SiteSettingsAPI.js';

export const EditOwnProfile = () => {
	const state: LoginState = useSelector<RootState, LoginState>(state => state.login);
	const [deleteEmail, setDeleteEmail] = useState<string>('');
	const [showEmailConfirm, setShowEmailConfirm] = useState<boolean>(false);
	const [deleteAccount] = useDeleteAccountMutation();
	const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
	const [triggerToast] = useIonToast();
	const { data: siteSettings } = useGetPublicInformationQuery();
	// Get the id from react router
	const { isError, data, error } = useGetOwnProfileQuery(state.user._id, { skip: deleteSuccess });
	// user is the local model object and keeper if unsaved updates.
	const [user, setUser] = useState<User | null>(null);
	const inputFile = useRef<any>(null);
	const [triggerDescription, { isError: updateIsError }] = useUpdateOwnUserDescriptionMutation();
	const [updateFeedPreferences, { isError: feedPreferencesUpdateIsError }] = useUpdateFeedPreferencesMutation();
	const [updateNotificationPreferences, { isError: notificationPreferencesUpdateIsError }] =
		useUpdateNotificationPreferencesMutation();
	const [updateEgnagementPreferences, { isError: engagementPreferencesUpdateIsError }] =
		useUpdateEngagementPreferencesMutation();
	const history = useHistory();
	const { loading, uploadImage } = useImageUpload(data, useAddProfileImageMutation);
	useLoading({ message: 'Setting profile photo...', isLoading: loading });
	const { data: blocks } = useGetUserBlocksQuery();
	const [present, dismiss] = useIonModal(BlockedPage, { user, onDismiss: () => dismiss() });
	const [presentHelp, dismissHelp] = useIonModal(HelpPopup, {
		contents: feedPreferencesHelpText,
		onDismiss: () => dismissHelp()
	});

	useEffect(() => {
		// Update the local copy, but only on first load, after that, the local copy is
		// assumed to be authoritative.
		if (!user && data) setUser(data);
	}, [data, user]);

	const { wrapper: updateDesc } = useDebounce(() => {
		if (!user) return null;
		return triggerDescription({ user });
	}, 1000);

	const selectImage = (e: any) => {
		uploadImage(e);
	};

	const onUploadClick = () => {
		inputFile.current.click();
	};
	const handlePreferences = (feedPreferences: FeedPreferences) => {
		if (user && feedPreferences) {
			setUser({ ...user, feedPreferences });
			updateFeedPreferences({ user: { ...user, feedPreferences } });
		}
	};

	const onNotificationPreferencesChange = (event: CheckboxCustomEvent) => {
		if (user === null) {
			return;
		}
		const updated: User = {
			...user,
			notificationPreferences: {
				email: event.target.checked
			}
		};
		setUser(updated);
		updateNotificationPreferences({ user: updated });
	};

	const onEngagementLevelChange = (event: CheckboxCustomEvent) => {
		if (user === null) {
			return;
		}
		const updated: User = {
			...user,
			engagementLevel: event.target.value
		};
		setUser(updated);
		updateEgnagementPreferences({ user: updated });
	};

	const [showEngagementHelp, dismissEngagementHelp] = useIonModal(HelpPopup, {
		contents: getEngagementHelpMarkdownText(),
		onDismiss: () => dismissEngagementHelp()
	});

	return (
		<QuirkIonPage name="Edit Profile">
			{data ? (
				<>
					<Header
						left={<IconButton icon={<IconArrowBack />} onClick={() => history.go(-1)} />}
						title="Edit Profile"
						right={
							<IconButton
								label="Logout"
								icon={<IconLogout />}
								onClick={() => history.push(`/main/settings/logout`)}
							/>
						}
					/>
					<QuirkIonContent>
						<div className={styles.paddingContainer}>
							<div className={styles.profilePicContainer}>
								<ProfilePic user={data} size="large" testId="profilePic" />
								<div className={classNames(styles.editLink, 'pointer')} onClick={onUploadClick}>
									Edit photo
								</div>

								<input
									type="file"
									name="profPicUpload"
									ref={inputFile}
									style={{ visibility: 'hidden', height: '0px' }}
									onChange={e => selectImage(e)}
								/>
							</div>
							<IonItem lines="none" className={classNames(styles.descriptionBox, styles.ionItem)}>
								<TextArea
									id="description"
									placeholder="Description"
									value={user?.description}
									name="description"
									rows={3}
									testId="description"
									onChange={e => {
										if (user) {
											const newUser: User = { ...user, description: e.target.value || '' };
											setUser(newUser);
											updateDesc(null);
										}
									}}
									error={!!error}
									disabled={false}
								/>
							</IonItem>
							<IonItem className={styles.ionItem}>
								<FixedField testId="email" label="Email" field={data?.email} />
							</IonItem>
							<IonItem className={styles.ionItem}>
								<FixedField testId="username" label="Username" field={data?.username} />
							</IonItem>
							<div className={styles.deleteEmailContainer}>
								<IonButton
									data-testid="deleteAccount"
									color={deleteEmail === user?.email ? 'danger' : 'primary'}
									fill="solid"
									size="small"
									className="radius8"
									expand="block"
									disabled={deleteEmail !== '' && deleteEmail !== user?.email}
									onClick={async () => {
										setShowEmailConfirm(true);
										if (deleteEmail === user?.email) {
											const result = await deleteAccount({ userid: user._id }).unwrap();
											if ('errorCode' in result) {
												setShowEmailConfirm(false);
												setDeleteEmail('');
												await triggerToast({
													message: result.errorMessage,
													color: 'danger',
													duration: 2000,
													position: 'top'
												});
											} else {
												setDeleteSuccess(true);
												await triggerToast({
													message: 'Account Deleted',
													color: 'success',
													duration: 2000,
													position: 'top'
												});
												setTimeout(() => {
													history.push('/main/settings/logout');
												}, 2000);
											}
										}
									}}
								>
									{deleteEmail !== user?.email ? 'Request Account Deletion' : 'Delete Account'}
								</IonButton>
								{showEmailConfirm && (
									<Input
										testId="deleteEmail"
										label="Confirm Email"
										value={deleteEmail}
										onChange={e => setDeleteEmail(e.target.value)}
										type="email"
										placeholder="Type email address to confirm deletion"
									/>
								)}
							</div>

							{siteSettings?.supportsPayments && (
								<TitleColumn title="Token balance">
									<TokenPurchase />
								</TitleColumn>
							)}

							{blocks && blocks.length > 0 ? (
								<IonItem className={styles.ionItem}>
									<FixedField
										label="Hidden users"
										field={blocks.length}
										testId="hiddenUsers"
										button={
											<IonButton
												data-testid="hiddenUsersButton"
												color={'primary'}
												fill="outline"
												size="small"
												className="radius8"
												onClick={() => present()}
												expand="block"
											>
												View hidden users
											</IonButton>
										}
									/>
								</IonItem>
							) : null}

							<div>
								<TitleColumn
									title={
										<div className={styles.withHelpButtonContainer}>
											<span>Feed Preferences</span>&emsp;
											<span className={styles.helpIcon}>
												<IconButton
													onClick={() => presentHelp()}
													icon={<IconHelp color="#fff" />}
												/>
											</span>
										</div>
									}
								>
									<div>
										<IonItem lines="none" className={styles.ionItem}>
											<Checkbox
												testId="fireHoseCheckbox"
												onClick={e => {
													if (!user) return;
													handlePreferences({
														...user?.feedPreferences,
														fireHose: e.target.checked
													});
												}}
												label="Fire hose"
												value={user?.feedPreferences.fireHose}
											/>
										</IonItem>
									</div>
									<div>
										<IonItem lines="none" className={styles.ionItem}>
											<Select
												className={styles.selectPadding}
												label="Tolerance for authors you dislike"
												value={user?.feedPreferences.enemiesThreshold}
												options={EnemiesThresholdOptions}
												testId="enemiesThresholdSelect"
												onChange={e => {
													if (!user) return;
													handlePreferences({
														...user?.feedPreferences,
														enemiesThreshold: e.target.value
													});
												}}
											></Select>
										</IonItem>
									</div>
									<div>
										<IonItem lines="none" className={styles.ionItem}>
											<Select
												label="Feed Volume"
												className={styles.selectPadding}
												value={user?.feedPreferences.volumePreference}
												options={VolumeOptions}
												testId="volumePreferenceSelect"
												onChange={e => {
													if (!user) return;
													handlePreferences({
														...user?.feedPreferences,
														volumePreference: e.target.value
													});
												}}
											></Select>
										</IonItem>
									</div>
									<TitleColumn titleClassName={styles.authorTitle} title="Author Preferences">
										<div>
											<IonItem lines="none" className={styles.ionItem}>
												<Checkbox
													disabled={user?.feedPreferences.fireHose}
													testId="preferFriendsCheckbox"
													onClick={e => {
														if (!user) return;
														handlePreferences({
															...user?.feedPreferences,
															preferFriends: e.target.checked
														});
													}}
													value={user?.feedPreferences.preferFriends}
													label="Friends"
												/>
											</IonItem>
										</div>

										<div>
											<IonItem lines="none" className={styles.ionItem}>
												<Checkbox
													testId={'preferFriendsOfFriendsCheckbox'}
													disabled={user?.feedPreferences.fireHose}
													onClick={e => {
														if (!user) return;
														handlePreferences({
															...user?.feedPreferences,
															preferFriendsOfFriends: e.target.checked
														});
													}}
													value={user?.feedPreferences.preferFriendsOfFriends}
													label="Friends of Friends"
												/>
											</IonItem>
										</div>

										<div>
											<IonItem lines="none" className={styles.ionItem}>
												<Checkbox
													disabled={user?.feedPreferences.fireHose}
													testId="preferPopularCheckbox"
													onClick={e => {
														if (!user) return;
														handlePreferences({
															...user?.feedPreferences,
															preferPopular: e.target.checked
														});
													}}
													value={user?.feedPreferences.preferPopular}
													label="Popular Authors"
												/>
											</IonItem>
										</div>
									</TitleColumn>
								</TitleColumn>
							</div>
							<div>
								<TitleColumn title="Notification Preferences">
									<div>
										<IonItem lines="none" className={styles.ionItem}>
											<Checkbox
												testId="emailNotificationsCheckbox"
												onClick={onNotificationPreferencesChange}
												value={user?.notificationPreferences.email}
												label="Email Notifications"
											/>
										</IonItem>
									</div>
								</TitleColumn>
							</div>
							<div>
								<TitleColumn
									title={
										<div className={styles.withHelpButtonContainer}>
											<span>Engagement Preferences</span>&emsp;
											<span className={styles.helpIcon}>
												<IconButton
													onClick={() => showEngagementHelp()}
													icon={<IconHelp color="#fff" />}
												/>
											</span>
										</div>
									}
								>
									<div>
										<IonItem lines="none" className={styles.ionItem}>
											<Select
												testId="engagementLevelSelect"
												label="Engagement Feedback"
												className={styles.selectPadding}
												value={user?.engagementLevel}
												options={getEngagementLevelsOptions()}
												onChange={onEngagementLevelChange}
											/>
										</IonItem>
									</div>
								</TitleColumn>
							</div>
						</div>
					</QuirkIonContent>
					<IonToast
						isOpen={isError}
						duration={1500}
						message={(error as FetchBaseQueryError)?.data as string}
						position="top"
						color="danger"
					/>
					<IonToast
						isOpen={updateIsError}
						duration={1500}
						message="Error updating user"
						position="top"
						color="danger"
					/>
					<IonToast
						isOpen={feedPreferencesUpdateIsError}
						duration={1500}
						message="Error updating Feed Preferences"
						position="top"
						color="danger"
					/>
					<IonToast
						isOpen={notificationPreferencesUpdateIsError}
						duration={1500}
						message="Error updating Notification Preferences"
						position="top"
						color="danger"
					/>
					<IonToast
						isOpen={engagementPreferencesUpdateIsError}
						duration={1500}
						message="Error updating Engagement Preferences"
						position="top"
						color="danger"
					/>
				</>
			) : null}
		</QuirkIonPage>
	);
};
